/**
 * Onboarding Tour — canonical PostHog event registry (KAN-3056 Phase 2)
 * =====================================================================
 * Phase 3 (KAN-3057) wires `useTourTelemetry()` into driver.js callbacks.
 * Defining names + property shapes here so call sites can't drift.
 *
 * Naming convention: snake_case event names, flat property keys
 * (matches existing AIghtBet pattern — see `signup_completed`, `upgrade_completed`,
 * `unlock_game_succeeded`, `loading_timeout` in src/posthog.ts users).
 *
 * Properties defined per KAN-3056 ticket scope:
 *   tier, breakpoint, entry_point, step, step_label, dwell_ms.
 * `auth_state` and `route` are added automatically by the hook (mirrors
 * `useLoadingTimeout`'s baseline so events join cleanly across surfaces).
 */

export const TOUR_EVENTS = {
  /** Welcome modal accepted — first step rendered. */
  STARTED: 'tour_started',
  /** User advanced past a step (Next clicked OR auto-advance fired). */
  STEP_COMPLETED: 'tour_step_completed',
  /** User went back one step. */
  STEP_BACK: 'tour_step_back',
  /** Skip button hit on any step before completion. */
  SKIPPED: 'tour_skipped',
  /** Closing CTA reached — full tour finished. */
  COMPLETED: 'tour_completed',
  /** Profile · Settings "Replay site tour" row clicked. */
  REPLAY_CLICKED: 'tour_replay_clicked',
} as const;

export type TourEventName = (typeof TOUR_EVENTS)[keyof typeof TOUR_EVENTS];

/**
 * Layout breakpoint at the moment the event fires.
 * Phase 3 derives this from a `useBreakpoint` hook or window.matchMedia;
 * Phase 2 only declares the type.
 */
export type TourBreakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Where the tour was triggered from. New-user gate vs explicit replay.
 */
export type TourEntryPoint = 'auto_first_login' | 'profile_replay';

/**
 * Properties common to every tour event. `tier`, `auth_state`, and `route`
 * are auto-attached by `useTourTelemetry`; callers don't pass them directly.
 */
export interface TourEventBaseProps {
  breakpoint: TourBreakpoint;
  entry_point: TourEntryPoint;
}

/** Per-step events: STEP_COMPLETED, STEP_BACK, SKIPPED. */
export interface TourStepEventProps extends TourEventBaseProps {
  /** Zero-based index in the 7-step sequence. */
  step: number;
  /** Stable identifier for the step — see Phase 3 step config. */
  step_label: string;
  /** Milliseconds the step was on screen before this transition. */
  dwell_ms: number;
}

/** STARTED has no step yet; tour just opened. */
export type TourStartedProps = TourEventBaseProps;

/** COMPLETED summarises the full run. */
export interface TourCompletedProps extends TourEventBaseProps {
  /** Total steps configured for this run (always 7 in v1). */
  total_steps: number;
  /** Total time from STARTED to COMPLETED, in milliseconds. */
  total_time_ms: number;
}

/** REPLAY_CLICKED fires from Profile · Settings before the tour gate re-arms. */
export type TourReplayClickedProps = Pick<TourEventBaseProps, 'breakpoint'>;
